import React, { useRef, useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { State } from '../../state';
import newsletterService from '../../services/newsletterService';
import { trackNewsletterSignup, trackEvent } from '../../util/analytics';
import styles from './NewsletterForm.module.css';

interface NewsletterFormProps {
  /** Attribution tag for where this form lives, e.g. "blog:some-slug". */
  source: string;
}

/**
 * Double opt-in newsletter signup. Posts the email to the backend, which stores
 * it as `pending` and emails a confirmation link. Bilingual, honeypot-protected,
 * and reports a `newsletter_signup` analytics conversion on success.
 */
const NewsletterForm: React.FC<NewsletterFormProps> = ({ source }) => {
  const [email, setEmail] = useState('');
  // Honeypot: hidden from real users; a filled value marks the request as spam.
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: ''
  });

  const { appLanguage, data: languageData } = useSelector((state: State) => state.languageReducer);

  // form_start once per mount, on first interaction (see ContactForm).
  const startedRef = useRef(false);
  const handleFormFocus = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent('form_start', { form: 'newsletter', source });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Silently drop bots: show success without calling the backend or tracking.
    if (honeypot) {
      setStatus({ type: 'success', message: languageData?.newsletter?.successMessage });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      await newsletterService.subscribe({
        email,
        language: appLanguage === 'tr' ? 'tr' : 'en',
        source
      });
      trackNewsletterSignup(source);
      setStatus({ type: 'success', message: languageData?.newsletter?.successMessage });
      setEmail('');
    } catch (error) {
      trackEvent('form_error', { form: 'newsletter', source });
      setStatus({ type: 'error', message: languageData?.newsletter?.errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className={styles.newsletter} aria-labelledby="newsletter-heading">
      <h2 id="newsletter-heading" className={styles.heading}>
        {languageData?.newsletter?.heading}
      </h2>
      <p className={styles.subtitle}>{languageData?.newsletter?.subtitle}</p>

      {status.type && (
        <Alert variant={status.type === 'success' ? 'success' : 'danger'} className={styles.alert}>
          {status.message}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} onFocus={handleFormFocus} className={styles.form}>
        {/* Honeypot: off-screen, out of tab order, hidden from assistive tech. */}
        <div className={styles.honeypot} aria-hidden="true">
          <label htmlFor="newsletter-website">Website</label>
          <input
            type="text"
            id="newsletter-website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className={styles.inputRow}>
          <Form.Control
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={languageData?.newsletter?.emailPlaceholder}
            aria-label={languageData?.newsletter?.emailPlaceholder}
            required
            className={styles.input}
          />
          <Button type="submit" variant="primary" disabled={isSubmitting} className={styles.button}>
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {languageData?.newsletter?.submitting}
              </>
            ) : (
              languageData?.newsletter?.submit
            )}
          </Button>
        </div>

        <p className={styles.consent}>{languageData?.newsletter?.consent}</p>
      </Form>
    </section>
  );
};

export default NewsletterForm;
